// Pure helpers for sorting search results by the engineer's prioritised
// basket branches (DEV-50739 SP-003). Kept free of React so Search.tsx can
// apply the same split to the current page and to the all-pages set.
import type { CartFulfillmentMethod } from './cartFulfillment'

/**
 * How a master matched a prioritised branch. In-stock matches lead the top
 * group; backorder matches follow (the branch can still supply it, just not
 * today).
 */
export type BranchPriorityRank = 'in_stock' | 'backorder'

/**
 * "CPS Manchester" — how a supplier + branch pair is named to the engineer.
 * Shared so the at-branch card, the pill row and the sheet all name a branch
 * the same way (DEV-51045).
 */
export function branchDisplayName(
  supplierName: string,
  branchName: string,
): string {
  return `${supplierName} ${branchName}`
}

const RANK_ORDER: Record<BranchPriorityRank, number> = {
  in_stock: 0,
  backorder: 1,
}

/**
 * One listing (raw product) that can supply a master from one branch, as
 * sent by the stock-check: its shelf / depot quantities (null = no figure,
 * or a backorder-only branch), stored price (null when unpriced) and id —
 * so the live price refresh can be matched to exactly this listing.
 */
export interface BranchStockListing {
  rawId: number | string | null
  branch: number | null
  depot: number | null
  price: number | null
  // This branch's distance from the search postcode (null when unknown).
  distanceMi: number | null
  // Quick add to cart: what a linked card needs to add this listing from
  // this branch without opening the product page.
  productName: string | null
  productCode: string | null
  // Pack size the quantity steps by (1 = sold singly).
  quantityMultiple: number
  // Which methods the branch serves.
  canCollect: boolean
  canDeliver: boolean
  // False only for an older response without the per-method figures below;
  // then the combined branch / depot figures stand in.
  hasMethodStock: boolean
  // Collection (branch shelf) and delivery quantity (including stock the
  // branch delivers from another branch), kept apart, with the date each is
  // available.
  collectionStock: number | null
  deliveryStock: number | null
  earliestCollection: string | null
  earliestDelivery: string | null
  // Supplier warehouse quantity and date, per method (date null when the
  // supplier gave none).
  pickupDepotStock: number | null
  deliveryDepotStock: number | null
  pickupDepotDate: string | null
  deliveryDepotDate: string | null
  backorderAllowed: boolean
  backorderCollectionDate: string | null
  backorderDeliveryDate: string | null
  // The backorder date is the supplier's own quote (Wolseley), which depends
  // on the quantity — re-checked at the engineer's quantity.
  backorderDateFromApi: boolean
}

// Counts above this render as "99+" on the card, as in the wireframe.
export const STOCK_COUNT_DISPLAY_CAP = 99

const formatStockCount = (quantity: number): string =>
  quantity > STOCK_COUNT_DISPLAY_CAP ? `${STOCK_COUNT_DISPLAY_CAP}+` : String(quantity)

/**
 * "12 in branch · 40 in supplier warehouse" for a linked in-stock card, for
 * the selected collection / delivery method. Depot is omitted when the
 * supplier gave no figure. Zero counts are shown (a method can have none
 * while the other has stock); null only when neither figure is known, so
 * the plain badge shows instead.
 */
export function formatBranchStockLabel(
  branchQuantity: number | null,
  depotQuantity: number | null,
): string | null {
  if (branchQuantity == null && depotQuantity == null) return null
  const branchQty = Math.max(branchQuantity ?? 0, 0)
  const parts = [`${formatStockCount(branchQty)} in branch`]
  if (depotQuantity != null) {
    parts.push(`${formatStockCount(depotQuantity)} in supplier warehouse`)
  }
  return parts.join(' · ')
}

/** One prioritised branch that can supply a master. */
export interface BranchPriorityMatch {
  supplierId: string
  branchId: string
  rank: BranchPriorityRank
  // Every listing that can supply the master from this branch, per the
  // stock-check (counts null for a backorder match); empty when the
  // response carried none.
  listings: BranchStockListing[]
}

/** Everything the search page knows about a master's prioritised matches. */
export interface BranchPriorityInfo {
  // Best rank across every match (in_stock beats backorder).
  rank: BranchPriorityRank
  matches: BranchPriorityMatch[]
}

// The slice of a stock-check response the ranking needs. Structural so the
// current-page and all-pages query lists (different generic types) both fit.
export interface BranchPriorityStockData {
  supplierId: number
  masterResults: Record<string, string>
  masterBranches: Record<string, string[]>
  masterBranchStock?: Record<string, Record<string, BranchStockListing[]>>
}

/**
 * Collect, per master, every prioritised branch that can supply it.
 *
 * A supplier reporting in_stock contributes an in_stock match for each
 * prioritised branch in its stocked-branch list; one reporting backorder
 * contributes backorder matches for its backorderable branches. Masters
 * with no match are absent from the map.
 */
export function buildPriorityInfoByMaster(
  stockQueries: ReadonlyArray<{ data?: BranchPriorityStockData }>,
  prioritisedBranchIds: ReadonlySet<string>,
): Map<string, BranchPriorityInfo> {
  const infoByMaster = new Map<string, BranchPriorityInfo>()
  if (prioritisedBranchIds.size === 0) return infoByMaster
  for (const query of stockQueries) {
    const stockData = query.data
    if (!stockData) continue
    const supplierId = String(stockData.supplierId)
    for (const [masterId, branchIds] of Object.entries(stockData.masterBranches ?? {})) {
      const status = stockData.masterResults?.[masterId]
      if (status !== 'in_stock' && status !== 'backorder') continue
      for (const branchId of branchIds) {
        if (!prioritisedBranchIds.has(branchId)) continue
        const match: BranchPriorityMatch = {
          supplierId,
          branchId,
          rank: status,
          listings: stockData.masterBranchStock?.[masterId]?.[branchId] ?? [],
        }
        const existing = infoByMaster.get(masterId)
        if (!existing) {
          infoByMaster.set(masterId, { rank: status, matches: [match] })
          continue
        }
        existing.matches.push(match)
        // in_stock beats backorder; never downgrade.
        if (RANK_ORDER[status] < RANK_ORDER[existing.rank]) existing.rank = status
      }
    }
  }
  return infoByMaster
}

/**
 * The ticked branch a grouped card's product page opens on (DEV-50739
 * SP-003): among the best-ranked matches (in stock beats backorder), the
 * branch whose cheapest listing is cheapest — live price where landed, else
 * stored — nearest on a tie. Null when nothing matched.
 */
export function pickPrioritisedBranchId(
  info: BranchPriorityInfo | undefined,
  livePriceByRawId: ReadonlyMap<string, number | null>,
): string | null {
  if (!info) return null
  let bestBranchId: string | null = null
  let bestPrice = Infinity
  let bestDistance = Infinity
  for (const match of info.matches) {
    if (match.rank !== info.rank) continue
    let price = Infinity
    let distance = Infinity
    for (const listing of match.listings) {
      const rawKey = listing.rawId != null ? String(listing.rawId) : null
      const listingPrice =
        (rawKey !== null && livePriceByRawId.has(rawKey)
          ? livePriceByRawId.get(rawKey)
          : listing.price) ?? Infinity
      if (listingPrice < price || (listingPrice === price && distance === Infinity)) {
        price = listingPrice
        distance = listing.distanceMi ?? Infinity
      }
    }
    if (
      bestBranchId === null ||
      price < bestPrice ||
      (price === bestPrice && distance < bestDistance)
    ) {
      bestBranchId = match.branchId
      bestPrice = price
      bestDistance = distance
    }
  }
  return bestBranchId
}

/** Names of a ticked branch, as the basket's "Show first" pill carries them. */
export interface PrioritisedBranchNames {
  supplierName: string
  branchName: string
  // Methods the basket's lines at this branch use (empty = none there).
  methods: ReadonlyArray<CartFulfillmentMethod>
}

/**
 * One linked product card (quick add to cart, QA-001): a single supplier
 * listing at a single ticked branch.
 */
export interface LinkedProductCardInfo {
  // `${rawId}:${branchId}` — unique per card.
  key: string
  rawId: string
  supplierId: string
  supplierName: string
  branchId: string
  branchName: string
  // Methods the basket already uses at this branch — the card's default.
  basketMethods: ReadonlyArray<CartFulfillmentMethod>
  rank: BranchPriorityRank
  // Live price once the refresh has landed for this listing, else stored.
  price: number | null
  listing: BranchStockListing
}

/** A row of the prioritised result list: a linked card or a grouped card. */
export type PrioritisedResultEntry<T> =
  | { kind: 'linked'; product: T; card: LinkedProductCardInfo }
  | { kind: 'master'; product: T }

const priceOrLast = (price: number | null): number => price ?? Infinity

/**
 * Expand an already-ordered list into linked product cards for every listing
 * at a ticked branch (QA-001): in-stock cards first, then backorder-only
 * ones, then the grouped cards of products no ticked branch can supply.
 * Each group keeps the incoming order of its products; one product's cards
 * run cheapest first (live price where landed), nearest on a tie.
 *
 * A listing can roll up to two products, so a card is kept only the first
 * time its listing + branch appears. A product whose matches produce no card
 * (no listings) stays a
 * grouped card.
 */
export function buildPrioritisedEntries<T extends { id: string }>(
  items: T[],
  infoByMaster: ReadonlyMap<string, BranchPriorityInfo>,
  branchNamesById: ReadonlyMap<string, PrioritisedBranchNames>,
  livePriceByRawId: ReadonlyMap<string, number | null>,
): PrioritisedResultEntry<T>[] {
  const inStockEntries: PrioritisedResultEntry<T>[] = []
  const backorderEntries: PrioritisedResultEntry<T>[] = []
  const otherEntries: PrioritisedResultEntry<T>[] = []
  const seenCardKeys = new Set<string>()
  for (const product of items) {
    const productCards: LinkedProductCardInfo[] = []
    for (const match of infoByMaster.get(String(product.id))?.matches ?? []) {
      const names = branchNamesById.get(match.branchId)
      if (!names) continue
      for (const listing of match.listings) {
        if (listing.rawId == null) continue
        const rawId = String(listing.rawId)
        const key = `${rawId}:${match.branchId}`
        if (seenCardKeys.has(key)) continue
        seenCardKeys.add(key)
        productCards.push({
          key,
          rawId,
          supplierId: match.supplierId,
          supplierName: names.supplierName,
          branchId: match.branchId,
          branchName: names.branchName,
          basketMethods: names.methods,
          rank: match.rank,
          price: livePriceByRawId.has(rawId)
            ? livePriceByRawId.get(rawId) ?? null
            : listing.price,
          listing,
        })
      }
    }
    if (productCards.length === 0) {
      otherEntries.push({ kind: 'master', product })
      continue
    }
    productCards.sort(
      (first, second) =>
        priceOrLast(first.price) - priceOrLast(second.price) ||
        (first.listing.distanceMi ?? Infinity) - (second.listing.distanceMi ?? Infinity),
    )
    for (const card of productCards) {
      const target = card.rank === 'in_stock' ? inStockEntries : backorderEntries
      target.push({ kind: 'linked', product, card })
    }
  }
  return [...inStockEntries, ...backorderEntries, ...otherEntries]
}
